import { useEffect, useState } from 'react';
import { Lock, Calendar, Clock } from 'lucide-react';

function formatDateTimePT(isoString) {
  if (!isoString) return { date: '—', time: '—' };
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return { date: '—', time: '—' };
  const date = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return { date, time };
}

function getRemaining(targetIso) {
  if (!targetIso) return null;
  const target = new Date(targetIso).getTime();
  const now = Date.now();
  const diff = Math.max(0, target - now);
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { totalSeconds, days, hours, minutes, seconds };
}

/**
 * GabaritoCountdown — Locked-state card with live countdown to gabarito release.
 * Props:
 *  - unlockAt: ISO datetime string
 *  - variant: "card" (full) | "compact" (inline)
 */
export function GabaritoCountdown({ unlockAt, variant = 'card' }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = getRemaining(unlockAt);
  const { date, time } = formatDateTimePT(unlockAt);
  const hasValidUnlock = !!unlockAt && !isNaN(new Date(unlockAt).getTime());
  // Re-read every second via state change
  void now;

  if (variant === 'compact') {
    if (!hasValidUnlock) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
          <Lock className="w-3 h-3" /> Aguardando Mestre
        </span>
      );
    }
    if (!remaining || remaining.totalSeconds === 0) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-bold">
          <Lock className="w-3 h-3 rotate-180" /> Liberado
        </span>
      );
    }
    const label = remaining.days > 0
      ? `${remaining.days}d ${remaining.hours}h`
      : remaining.hours > 0
        ? `${remaining.hours}h ${String(remaining.minutes).padStart(2, '0')}m`
        : `${remaining.minutes}m ${String(remaining.seconds).padStart(2, '0')}s`;
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full" data-testid="gabarito-countdown-compact">
        <Lock className="w-3 h-3" /> {label}
      </span>
    );
  }

  return (
    <div
      data-testid="gabarito-countdown-card"
      className="relative overflow-hidden rounded-2xl bg-slate-900 text-white p-5 shadow-xl"
    >
      <div className="absolute -right-12 -top-12 w-44 h-44 rounded-full bg-amber-500/15 blur-3xl" />
      <div className="absolute -left-8 -bottom-8 w-32 h-32 rounded-full bg-violet-500/15 blur-3xl" />

      <div className="relative">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
            <Lock className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300">
              Gabarito Selado pelo Mestre
            </div>
            <h3 className="text-lg font-outfit font-bold leading-tight">Sua nota foi registrada!</h3>
          </div>
        </div>

        {hasValidUnlock ? (
          <p className="text-sm text-slate-200 leading-relaxed mb-4">
            O gabarito detalhado será liberado para toda a turma em{' '}
            <span className="inline-flex items-center gap-1 font-bold text-white">
              <Calendar className="w-3.5 h-3.5 text-amber-300" /> {date}
            </span>{' '}
            às{' '}
            <span className="inline-flex items-center gap-1 font-bold text-white">
              <Clock className="w-3.5 h-3.5 text-amber-300" /> {time}
            </span>
            .
          </p>
        ) : (
          <p className="text-sm text-slate-200 leading-relaxed mb-4">
            Aguardando o Mestre liberar manualmente o gabarito desta missão.
          </p>
        )}

        {hasValidUnlock && remaining && remaining.totalSeconds > 0 && (
          <div className="grid grid-cols-4 gap-2" data-testid="gabarito-countdown-grid">
            {[
              { label: 'Dias', value: remaining.days },
              { label: 'Horas', value: remaining.hours },
              { label: 'Min', value: remaining.minutes },
              { label: 'Seg', value: remaining.seconds }
            ].map((seg) => (
              <div
                key={seg.label}
                className="rounded-xl bg-white/5 border border-white/10 p-3 text-center backdrop-blur-sm"
              >
                <div className="text-2xl font-extrabold tabular-nums leading-none">
                  {String(seg.value).padStart(2, '0')}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">
                  {seg.label}
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="mt-4 text-xs text-slate-400 italic">
          Enquanto o tempo corre, revise seu raciocínio — o Mestre observa todos os Treinadores.
        </p>
      </div>
    </div>
  );
}

export default GabaritoCountdown;
